import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface NavbarProps {
  scrolled: boolean;
  onProjectsClick?: (e: React.MouseEvent) => void;
  onContactClick?: (e: React.MouseEvent) => void;
}

export default function Navbar({ scrolled, onProjectsClick, onContactClick }: NavbarProps) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProjectsClick = (e: React.MouseEvent) => {
    setMobileMenuOpen(false);
    if (onProjectsClick) {
      onProjectsClick(e);
    } else if (isHome) {
      e.preventDefault();
      const projectsSection = document.getElementById('projetos');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleContactClick = (e: React.MouseEvent) => {
    setMobileMenuOpen(false);
    if (onContactClick) {
      onContactClick(e);
    } else if (isHome) {
      e.preventDefault();
      const contactSection = document.getElementById('contato');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20 sm:h-28">
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src="https://static.readdy.ai/image/c30d1200a69d27266a785c8d79b381ff/57e5611cc65ba6dce761df2e05562b72.png" 
              alt="Criarquitetura - Arquiteto em Ilhabela"
              className="h-16 sm:h-20 md:h-24 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            <Link
              to="/"
              className={`text-sm font-inter font-medium transition-colors duration-300 hover:text-accent whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Home
            </Link>
            <Link
              to="/sobre"
              className={`text-sm font-inter font-medium transition-colors duration-300 hover:text-accent whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Sobre
            </Link>
            <Link
              to="/servicos"
              className={`text-sm font-inter font-medium transition-colors duration-300 hover:text-accent whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Serviços
            </Link>
            <a
              href={isHome ? "#projetos" : "/"}
              onClick={handleProjectsClick}
              className={`text-sm font-inter font-medium transition-colors duration-300 hover:text-accent whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Projetos
            </a>
            <a
              href={isHome ? "#contato" : "/"}
              onClick={handleContactClick}
              className={`text-sm font-inter font-medium transition-colors duration-300 hover:text-accent whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Contato
            </a>
          </div>

          <a
            href="https://wa.me/5512974034577"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-2 px-6 py-3 bg-accent text-white text-sm font-inter font-medium rounded-full hover:bg-accent/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Falar com Arquiteto
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label="Menu"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-white shadow-lg"
        >
          <div className="px-4 py-6 space-y-4">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-base font-inter font-medium text-gray-900 hover:text-accent transition-colors cursor-pointer py-2"
            >
              Home
            </Link>
            <Link
              to="/sobre"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-base font-inter font-medium text-gray-900 hover:text-accent transition-colors cursor-pointer py-2"
            >
              Sobre
            </Link>
            <Link
              to="/servicos"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-base font-inter font-medium text-gray-900 hover:text-accent transition-colors cursor-pointer py-2"
            >
              Serviços
            </Link>
            <a
              href={isHome ? "#projetos" : "/"}
              onClick={handleProjectsClick}
              className="block text-base font-inter font-medium text-gray-900 hover:text-accent transition-colors cursor-pointer py-2"
            >
              Projetos
            </a>
            <a
              href={isHome ? "#contato" : "/"}
              onClick={handleContactClick}
              className="block text-base font-inter font-medium text-gray-900 hover:text-accent transition-colors cursor-pointer py-2"
            >
              Contato
            </a>
            <a
              href="https://wa.me/5512974034577"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white text-base font-inter font-medium rounded-full hover:bg-accent/90 transition-all duration-300 whitespace-nowrap cursor-pointer mt-4"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Falar com Arquiteto
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
